import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Heart, MapPin, Plus, RefreshCw, AlertCircle, Bell, BellOff, X, Star, Briefcase } from 'lucide-react';
import { Input } from '../components/ui/input';
import { CategoryHeader, CategorySearchBar, STUDENT_LEVEL_KEY } from '../components/student-hub';
import '../components/student-hub/CategoryComponents.css';
import { fetchStudentHubItems, ACADEMIC_LEVELS, CATEGORY_CONFIG } from '../services/studentHubApi';
import './StudentHub.css';
import './StudentBooksPage.css';

// Category to API category mapping
const CATEGORY_MAP = {
  'internships-jobs': 'internships_jobs',
  'scholarships': 'scholarships',
  'admissions': 'universities_admissions',
  'courses-skills': 'courses_learning',
};

import { useUnifiedSearch } from '../hooks/useUnifiedSearch';

// Icons
const BriefcaseIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);

// Debounce delay
const SEARCH_DEBOUNCE = 300;

export default function StudentListPage({ category }) {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl' || isArabic;
  const lang = isRTL ? 'ar' : 'en';
  
  // Map frontend category to API category
  const apiCategory = CATEGORY_MAP[category] || 'internships_jobs';
  
  // Get config for current category
  const getConfig = () => {
    const configKey = apiCategory;
    return CATEGORY_CONFIG[configKey] || {
      title: { en: 'Opportunities', ar: 'فرص' },
      searchHint: { en: 'Search...', ar: 'بحث...' },
      icon: '💼',
    };
  };
  const config = getConfig();
  
  // Get user's selected level from localStorage
  const getUserLevel = () => {
    try {
      return localStorage.getItem(STUDENT_LEVEL_KEY) || '';
    } catch {
      return '';
    }
  };
  
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  
  // Filters state
  const [filters, setFilters] = useState({
    filterLevel: searchParams.get('level') || '',
    locationType: searchParams.get('locationType') || '',
    type: searchParams.get('type') || '',
    city: searchParams.get('city') || '',
    isPaid: searchParams.get('isPaid') || '',
    sort: searchParams.get('sort') || 'newest',
  });
  
  // Refs for debouncing and abort
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  
  // Fetch items from API
  const fetchItems = useCallback(async (query, filtersOverride, skipCache = false) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    const currentFilters = filtersOverride || filters;
    const userLevel = getUserLevel();
    const effectiveLevel = currentFilters.filterLevel || userLevel || '';
    
    try {
      const result = await fetchStudentHubItems({
        category: apiCategory,
        level: effectiveLevel || undefined,
        q: query || undefined,
        locationType: currentFilters.locationType || undefined,
        type: currentFilters.type || undefined,
        city: currentFilters.city || undefined,
        isPaid: currentFilters.isPaid || undefined,
        sort: currentFilters.sort,
        limit: 50,
        offset: 0,
        signal: abortControllerRef.current.signal,
        skipCache,
      });
      
      if (isMountedRef.current) {
        setItems(result.items || []);
        setTotal(result.total || 0);
        setError(null);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      
      if (isMountedRef.current) {
        setError(err.message || 'Failed to load items');
        setItems([]);
        setTotal(0);
      }
    }
  }, [apiCategory, filters]);
  
  // Initial load
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    
    fetchItems(searchQuery).finally(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    });
    
    // Fetch saved items if logged in
    if (user && token) {
      fetchSavedItems();
    }
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [apiCategory]);
  
  // Refetch when filters change
  useEffect(() => {
    setLoading(true);
    fetchItems(searchQuery).finally(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    });
  }, [filters.filterLevel, filters.locationType, filters.type, filters.city, filters.isPaid, filters.sort]);
  
  // Debounced search handler
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      setLoading(true);
      fetchItems(value).finally(() => {
        if (isMountedRef.current) {
          setLoading(false);
        }
      });
    }, SEARCH_DEBOUNCE);
  }, [fetchItems]);
  
  // Search submit handler
  const handleSearchSubmit = useCallback((term) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    const newParams = new URLSearchParams(searchParams);
    if (term) {
      newParams.set('q', term);
    } else {
      newParams.delete('q');
    }
    setSearchParams(newParams);
    
    setLoading(true);
    fetchItems(term).finally(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    });
  }, [searchParams, setSearchParams, fetchItems]);
  
  // Retry handler
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchItems(searchQuery, filters, true).finally(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    });
  }, [searchQuery, filters, fetchItems]);

  // ═══════════════════════════════════════════════════════════
  // UNIFIED SEARCH PIPELINE — single source of truth
  // normalize → filter → dedupe (shared with Home, Shop, etc.)
  // ═══════════════════════════════════════════════════════════
  const displayItems = useUnifiedSearch(items, searchQuery, { idKey: 'opp_id' });

  // Fetch saved items
  const fetchSavedItems = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedItems(data.saved_items || []);
      }
    } catch (e) { console.error(e); }
  };
  
  // Toggle save
  const toggleSave = async (itemId) => {
    if (!user || !token) {
      navigate('/welcome');
      return;
    }
    
    const itemType = 'opportunity';
    const isSavedNow = savedItems.some(s => s.item_type === itemType && s.item_id === itemId);
    
    try {
      if (isSavedNow) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items/${itemType}/${itemId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSavedItems(prev => prev.filter(s => !(s.item_type === itemType && s.item_id === itemId)));
      } else {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-items`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ item_type: itemType, item_id: itemId })
        });
        setSavedItems(prev => [...prev, { item_type: itemType, item_id: itemId }]);
      }
    } catch (e) { console.error(e); }
  };
  
  const isSaved = (itemId) => savedItems.some(s => s.item_type === 'opportunity' && s.item_id === itemId);
  
  const handleBack = () => {
    if (hasInAppHistory()) return navigate(-1);
    navigate(location.state?.from || '/student-hub', { replace: true });
  };
  
  const getLevelLabel = (key) => {
    const level = ACADEMIC_LEVELS.find(l => l.key === key);
    if (!level) return '';
    return isRTL ? level.label_ar : level.label_en;
  };
  
  const hasActiveFilters = filters.filterLevel || filters.locationType || filters.type || filters.city || filters.isPaid || filters.sort !== 'newest';
  
  const clearFilters = () => {
    setFilters({
      filterLevel: '', locationType: '', type: '', city: '', isPaid: '', sort: 'newest'
    });
    setShowFilters(false);
  };
  
  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    return date.toLocaleDateString(lang === 'ar' ? 'ar-SD' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  const handleLevelChange = useCallback(() => {
    navigate('/student-hub', { state: { showLevelPicker: true } });
  }, [navigate]);
  
  // CTA config by category
  const getCTAConfig = () => {
    switch (apiCategory) {
      case 'internships_jobs':
        return { route: '/student-hub/opportunities/new', label: { en: 'Post Opportunity', ar: 'أضف فرصة' } };
      case 'scholarships':
        return { route: '/student-hub/opportunities/new', label: { en: 'Post Scholarship', ar: 'أضف منحة' } };
      case 'universities_admissions':
        return { route: '/student-hub/opportunities/new', label: { en: 'Post Admission Info', ar: 'أضف معلومات تقديم' } };
      case 'courses_learning':
        return { route: '/student-hub/opportunities/new', label: { en: 'Post Course', ar: 'أضف دورة' } };
      default:
        return { route: '/student-hub/opportunities/new', label: { en: 'Post', ar: 'أضف' } };
    }
  };
  const ctaConfig = getCTAConfig();
  
  // Recommended jobs (only for internships_jobs)
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  // Job alerts
  const [jobAlerts, setJobAlerts] = useState([]);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [alertKeywords, setAlertKeywords] = useState('');
  const [alertType, setAlertType] = useState('');

  useEffect(() => {
    if (apiCategory === 'internships_jobs' && user && token) {
      fetchRecommendations();
      fetchJobAlerts();
    }
  }, [apiCategory, user, token]);

  const fetchRecommendations = async () => {
    setLoadingRecs(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/recommended?limit=4`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecommendations(data.recommendations || []);
      }
    } catch (e) { console.error(e); }
    setLoadingRecs(false);
  };

  const fetchJobAlerts = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setJobAlerts(data.alerts || []);
      }
    } catch (e) { console.error(e); }
  };

  const createJobAlert = async () => {
    if (!alertKeywords.trim()) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-alerts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keywords: alertKeywords.split(',').map(k => k.trim()).filter(Boolean),
          type: alertType || '',
          city: filters.city || '',
        }),
      });
      if (res.ok) {
        setShowAlertForm(false);
        setAlertKeywords('');
        setAlertType('');
        fetchJobAlerts();
      }
    } catch (e) { console.error(e); }
  };

  const deleteJobAlert = async (alertId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-alerts/${alertId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setJobAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    } catch (e) { console.error(e); }
  };
  const renderSkeletons = () => (
    <div className="opps-list">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="opp-card-skeleton">
          <Skeleton className="skeleton-title" style={{ height: 20, marginBottom: 8 }} />
          <Skeleton className="skeleton-text" style={{ width: '60%', marginBottom: 12 }} />
          <Skeleton className="skeleton-badges" style={{ height: 24, width: '80%' }} />
        </div>
      ))}
    </div>
  );
  
  // Render error state
  const renderError = () => (
    <div className="error-state" data-testid="error-state">
      <AlertCircle size={48} className="error-icon" />
      <p className="error-message">{error}</p>
      <Button variant="outline" onClick={handleRetry} className="retry-btn">
        <RefreshCw size={16} />
        {t('had.retry')}
      </Button>
    </div>
  );
  
  // Render empty state
  const renderEmpty = () => (
    <div className="empty-state" data-testid="empty-state">
      <BriefcaseIcon />
      <p>{t('slp.noResultsForThisLevel')}</p>
      {getUserLevel() && (
        <Button variant="outline" onClick={handleLevelChange} className="change-level-btn">
          {t('sbp.changeLevel')}
        </Button>
      )}
      {hasActiveFilters && (
        <Button variant="outline" onClick={clearFilters} style={{ marginTop: 8 }}>
          {t('slp.clearFilters')}
        </Button>
      )}
    </div>
  );
  
  return (
    <div className="student-list-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid={`student-list-${category}`}>
      {/* Reusable Header */}
      <CategoryHeader
        icon={config.icon}
        title={config.title}
        onBack={handleBack}
        showLevel={true}
      />
      
      {/* Reusable Search Bar */}
      <CategorySearchBar
        category={category}
        placeholder={config.searchHint}
        value={searchQuery}
        onChange={handleSearchChange}
        onSubmit={handleSearchSubmit}
        onFilterClick={() => setShowFilters(true)}
        hasActiveFilters={hasActiveFilters}
      />
      
      {/* Filter Sheet */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent side={isRTL ? 'left' : 'right'} className="filter-sheet">
          <SheetHeader>
            <SheetTitle>{t('home.filters')}</SheetTitle>
          </SheetHeader>
          
          <div className="filter-sheet-content">
            {/* Level Filter */}
            <div className="filter-group">
              <label>{t('onborole.academicLevel')}</label>
              <Select value={filters.filterLevel || 'all'} onValueChange={(v) => setFilters(p => ({ ...p, filterLevel: v === 'all' ? '' : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder={t('slp.selectLevel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('slp.all')}</SelectItem>
                  {ACADEMIC_LEVELS.map(l => (
                    <SelectItem key={l.key} value={l.key}>{isRTL ? l.label_ar : l.label_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Location Type Filter */}
            <div className="filter-group">
              <label>{t('slp.workType')}</label>
              <Select value={filters.locationType || 'all'} onValueChange={(v) => setFilters(p => ({ ...p, locationType: v === 'all' ? '' : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder={t('slp.select')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('slp.all')}</SelectItem>
                  <SelectItem value="remote">{t('slp.remote')}</SelectItem>
                  <SelectItem value="onsite">{t('slp.onsite')}</SelectItem>
                  <SelectItem value="hybrid">{t('slp.hybrid')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Type Filter (Job/Internship) */}
            {apiCategory === 'internships_jobs' && (
              <div className="filter-group">
                <label>{isRTL ? 'النوع' : 'Type'}</label>
                <Select value={filters.type || 'all'} onValueChange={(v) => setFilters(p => ({ ...p, type: v === 'all' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('slp.all')}</SelectItem>
                    <SelectItem value="internship">{isRTL ? 'تدريب' : 'Internship'}</SelectItem>
                    <SelectItem value="job">{isRTL ? 'وظيفة' : 'Job'}</SelectItem>
                    <SelectItem value="volunteer">{isRTL ? 'تطوع' : 'Volunteer'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* City Filter */}
            {apiCategory === 'internships_jobs' && (
              <div className="filter-group">
                <label>{isRTL ? 'المدينة' : 'City'}</label>
                <Select value={filters.city || 'all'} onValueChange={(v) => setFilters(p => ({ ...p, city: v === 'all' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-city">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('slp.all')}</SelectItem>
                    <SelectItem value="khartoum">{isRTL ? 'الخرطوم' : 'Khartoum'}</SelectItem>
                    <SelectItem value="omdurman">{isRTL ? 'أم درمان' : 'Omdurman'}</SelectItem>
                    <SelectItem value="port_sudan">{isRTL ? 'بورتسودان' : 'Port Sudan'}</SelectItem>
                    <SelectItem value="kassala">{isRTL ? 'كسلا' : 'Kassala'}</SelectItem>
                    <SelectItem value="gedarif">{isRTL ? 'القضارف' : 'Gedarif'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Paid Filter */}
            {apiCategory === 'internships_jobs' && (
              <div className="filter-group">
                <label>{isRTL ? 'مدفوع / غير مدفوع' : 'Paid / Unpaid'}</label>
                <Select value={filters.isPaid || 'all'} onValueChange={(v) => setFilters(p => ({ ...p, isPaid: v === 'all' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-paid">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('slp.all')}</SelectItem>
                    <SelectItem value="true">{isRTL ? 'مدفوع' : 'Paid'}</SelectItem>
                    <SelectItem value="false">{isRTL ? 'غير مدفوع' : 'Unpaid'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Sort */}
            <div className="filter-group">
              <label>{t('slp.sortBy')}</label>
              <Select value={filters.sort} onValueChange={(v) => setFilters(p => ({ ...p, sort: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('home.newest')}</SelectItem>
                  <SelectItem value="oldest">{t('slp.oldest')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters} className="clear-filters-btn">
                {t('slp.clearFilters')}
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Recommended Jobs Section */}
      {apiCategory === 'internships_jobs' && user && recommendations.length > 0 && (
        <div className="recs-section" data-testid="recommended-jobs">
          <div className="recs-header">
            <Star size={16} />
            <h3>{isRTL ? 'موصى به لك' : 'Recommended for You'}</h3>
          </div>
          <div className="recs-scroll">
            {recommendations.map(rec => (
              <div
                key={rec.id}
                className="rec-chip"
                onClick={() => navigate(`/student-hub/opportunities/${rec.id}`, { state: { from: location.pathname } })}
                data-testid={`rec-${rec.id}`}
              >
                <span className="rec-title">{isRTL ? rec.title_ar || rec.title : rec.title}</span>
                <span className="rec-org">{rec.organization}</span>
                {rec.matched_skills?.length > 0 && (
                  <div className="rec-match">
                    {rec.matched_skills.slice(0, 2).map((s, i) => <Badge key={i} variant="outline" className="rec-skill">{s}</Badge>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Job Alerts */}
      {apiCategory === 'internships_jobs' && user && (
        <div className="alerts-bar" data-testid="job-alerts">
          {jobAlerts.length > 0 ? (
            <div className="alerts-list">
              <Bell size={14} className="alerts-icon" />
              {jobAlerts.map(alert => (
                <span key={alert.alert_id} className="alert-tag">
                  {alert.keywords?.join(', ') || alert.type || (isRTL ? 'تنبيه' : 'Alert')}
                  <button onClick={() => deleteJobAlert(alert.alert_id)} className="alert-remove"><X size={12} /></button>
                </span>
              ))}
            </div>
          ) : null}
          {showAlertForm ? (
            <div className="alert-form">
              <Input
                value={alertKeywords}
                onChange={e => setAlertKeywords(e.target.value)}
                placeholder={isRTL ? 'كلمات مفتاحية (مفصولة بفاصلة)' : 'Keywords (comma-separated)'}
                className="alert-input"
                data-testid="alert-keywords-input"
              />
              <Button size="sm" onClick={createJobAlert} data-testid="save-alert-btn">
                {isRTL ? 'حفظ' : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAlertForm(false)}>
                <X size={14} />
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowAlertForm(true)} className="create-alert-btn" data-testid="create-alert-btn">
              <Bell size={14} /> {isRTL ? 'تنبيه وظائف' : 'Job Alert'}
            </Button>
          )}
        </div>
      )}

      {/* Results */}
      <div className="books-content">
        {loading ? (
          renderSkeletons()
        ) : error ? (
          renderError()
        ) : displayItems.length === 0 ? (
          renderEmpty()
        ) : (
          <div className="opps-list">
            {displayItems.map(item => (
              <div 
                key={item.id}
                className="opp-card"
                onClick={() => navigate(`/student-hub/opportunities/${item.id}`, { state: { from: location.pathname } })}
                data-testid={`item-card-${item.id}`}
              >
                <div className="opp-card-header">
                  <div className="opp-info">
                    <h3 className="opp-title">{isRTL && item.title_ar ? item.title_ar : item.title}</h3>
                    <p className="opp-org">{item.organization || item.instructor}</p>
                  </div>
                  <button
                    className={`save-btn-small ${isSaved(item.id) ? 'saved' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSave(item.id);
                    }}
                    data-testid={`save-btn-${item.id}`}
                  >
                    <Heart size={18} fill={isSaved(item.id) ? 'currentColor' : 'none'} />
                  </button>
                </div>
                
                <div className="opp-badges">
                  <Badge className={`type-badge type-${item.type}`}>
                    {item.type === 'job' ? (t('slp.job')) :
                     item.type === 'internship' ? (t('slp.internship')) :
                     item.type === 'volunteer' ? (t('slp.volunteer')) :
                     item.type === 'scholarship' ? (t('slp.scholarship')) :
                     item.type === 'admission' ? (t('slp.admission')) :
                     item.type === 'guide' ? (t('slp.guide')) :
                     item.type === 'course' ? (t('slp.course')) :
                     (t('slp.opportunity'))}
                  </Badge>
                  {item.locationType && (
                    <Badge variant="outline">
                      {item.locationType === 'remote' ? (t('slp.remote')) :
                       item.locationType === 'onsite' ? (t('slp.onsite')) :
                       (t('slp.hybrid'))}
                    </Badge>
                  )}
                  {item.isPaid && (
                    <Badge className="paid-badge">{t('slp.paid')}</Badge>
                  )}
                  {item.coverage && (
                    <Badge variant="outline" className="coverage-badge">{item.coverage}</Badge>
                  )}
                  {item.country && (
                    <Badge variant="outline" className="country-badge">{item.country}</Badge>
                  )}
                </div>
                
                {item.city && item.city !== 'remote' && item.city !== 'multiple' && (
                  <div className="opp-location">
                    <MapPin size={14} />
                    <span>{item.city}</span>
                  </div>
                )}
                
                {item.deadline && (
                  <p className="opp-deadline">
                    {t('slp.deadline')} {formatDeadline(item.deadline)}
                  </p>
                )}
                
                {/* Card Apply Button */}
                {apiCategory === 'internships_jobs' && (
                  <div className="opp-card-actions">
                    <Button
                      size="sm"
                      className="card-apply-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/student-hub/opportunities/${item.id}`, { state: { from: location.pathname, autoApply: true } });
                      }}
                      data-testid={`apply-btn-${item.id}`}
                    >
                      {isRTL ? 'تقديم' : 'Apply'}
                    </Button>
                  </div>
                )}
                
                {item.level && item.level.length > 0 && (
                  <Badge variant="outline" className="level-tag-small">
                    {getLevelLabel(Array.isArray(item.level) ? item.level[0] : item.level)}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Results count */}
        {!loading && !error && displayItems.length > 0 && (
          <div className="results-count">
            {isRTL ? `${total} نتيجة` : `${total} results`}
          </div>
        )}
      </div>
      
      {/* CTA Button */}
      {user && (
        <div className="sticky-cta">
          <Button 
            className="cta-btn"
            onClick={() => navigate(ctaConfig.route, { state: { from: location.pathname } })}
            data-testid="cta-create"
          >
            <Plus size={20} />
            {isRTL ? ctaConfig.label.ar : ctaConfig.label.en}
          </Button>
        </div>
      )}
    </div>
  );
}
